import { useState } from 'react';
import { Modal, Typography, Space, Tag, Alert, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWalletConnect } from '@/hooks/useWalletConnect';
import { useArmDelay } from '@/hooks/useArmDelay';
import { chainMetaByKey } from '@/lib/walletconnect/chains';
import { dappIdentityAllowsApproval } from '@/lib/walletconnect/verify';
import { EvmCallList } from '@/components/vaults/EvmSignedCalls';
import { formatBalance } from '@/types/vault';
import { DappIdentityNotice } from './DappIdentityNotice';

const { Text } = Typography;

/**
 * Shown when a connected dApp requests a transaction (eth_sendTransaction). The
 * user reviews it and turns it into an M-of-N proposal — it does NOT execute here.
 *
 * Requests are queued (the head is shown; new ones never replace it), the
 * origin comes from WalletConnect Verify, and the calldata is decoded with the
 * same call-safety rules the devices apply (ENTX-A-4 / ENTX-A-1).
 */
export function DappTxRequestModal() {
  const { t } = useTranslation();
  const wc = useWalletConnect();
  const [acknowledged, setAcknowledged] = useState(false);

  const req = wc?.pendingTxRequest ?? null;
  const open = !!req;
  const requestKey = req ? `${req.request.topic}:${String(req.request.id)}` : null;
  const armed = useArmDelay(requestKey);

  const chainMeta = req ? chainMetaByKey(req.binding.chain) : undefined;
  const call = req?.call;
  const isApprovalLike =
    !!call &&
    (call.kind === 'erc20_approve' ||
      call.kind === 'erc20_increase_allowance' ||
      call.kind === 'set_approval_for_all' ||
      call.kind === 'erc20_permit');
  const canCreate =
    !!req && armed && !req.call.blocked && dappIdentityAllowsApproval(req.identity, acknowledged);

  return (
    <Modal
      open={open}
      title={t('walletconnect.tx.title')}
      okText={t('walletconnect.tx.create_proposal')}
      cancelText={t('walletconnect.tx.reject')}
      onOk={() => {
        if (canCreate) void wc?.confirmTxRequest();
      }}
      onCancel={() => void wc?.rejectTxRequest()}
      confirmLoading={wc?.txRequestSubmitting}
      okButtonProps={{ disabled: !canCreate }}
      destroyOnHidden
      width={560}
    >
      {req && call && (
        <Space
          key={requestKey ?? undefined}
          orientation="vertical"
          size="middle"
          style={{ width: '100%' }}
        >
          {(wc?.txQueueSize ?? 0) > 1 && (
            <Alert
              type="info"
              showIcon
              title={t('walletconnect.verify.queue_position', { count: wc?.txQueueSize ?? 0 })}
            />
          )}
          <DappIdentityNotice identity={req.identity} onAcknowledgedChange={setAcknowledged} />

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('walletconnect.tx.vault')}>
              {req.binding.vaultName} {chainMeta && <Tag>{chainMeta.name}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('walletconnect.tx.to')}>
              <Text code style={{ fontSize: 12 }}>
                {call.target}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('walletconnect.tx.value')}>
              {formatBalance(call.value, req.binding.chain)} {chainMeta?.symbol}
            </Descriptions.Item>
          </Descriptions>

          {call.data !== '0x' && (
            <div>
              <Text type="secondary">{t('walletconnect.tx.decoded')}:</Text>
              <EvmCallList calls={[call]} chain={req.binding.chain} />
            </div>
          )}
          {isApprovalLike && (
            <Alert type="error" showIcon title={t('walletconnect.tx.approval_warning')} />
          )}

          <Alert type="info" showIcon title={t('walletconnect.tx.proposal_note')} />
        </Space>
      )}
    </Modal>
  );
}
